import type { LocalityRef, LocalityStatus } from "@/types/localities";

/**
 * Locality (НП) helpers — Sprint 228, design spec §1.
 *
 * ONE module for every reading of a row's locality, so the card, the list
 * badge, the planning warning and the route-template editor cannot disagree
 * about which rows are a problem.
 */

const LOCALITY_STATUSES: readonly LocalityStatus[] = [
  "",
  "auto",
  "manual",
  "ambiguous",
  "not_found",
];

/**
 * Narrows a wire value to a known status. An absent field (a fixture, or a
 * cached payload from before the deploy) and an unknown future value both read
 * as `""` — "not resolved yet" — never as a guess.
 */
export function normaliseLocalityStatus(
  status: string | null | undefined,
): LocalityStatus {
  return LOCALITY_STATUSES.includes(status as LocalityStatus)
    ? (status as LocalityStatus)
    : "";
}

/**
 * «Минский район» → «Минский р-н». Only a trailing standalone word `район` is
 * shortened (case-sensitive on the Russian word); everything else is left
 * alone. `null`/blank → `null`.
 */
export function shortenDistrict(
  district: string | null | undefined,
): string | null {
  const trimmed = (district ?? "").trim();
  if (!trimmed) return null;
  return trimmed.replace(/(^|\s)район$/u, "$1р-н");
}

/**
 * «Новосёлки, Минский р-н», or just «Новосёлки» when there is no district.
 * The region is never part of the line — only the picker shows it.
 */
export function formatLocality(loc: LocalityRef): string {
  const district = shortenDistrict(loc.district);
  return district ? `${loc.name}, ${district}` : loc.name;
}

/**
 * Whether a coordinate pair is a real point. `0,0` counts as "not set" — the
 * app-wide convention; without it a blank coordinate drops a pin in the Gulf of
 * Guinea. Accepts DRF decimal strings, numbers or null.
 *
 * Lifted from the two copies in `OrderViewWhere` / `TemplateViewWhere` so the
 * locality predicate below reuses the SAME rule rather than a third copy.
 */
export function hasValidCoords(
  lat: string | number | null | undefined,
  lon: string | number | null | undefined,
): boolean {
  const latNum = typeof lat === "number" ? lat : parseFloat(lat ?? "");
  const lonNum = typeof lon === "number" ? lon : parseFloat(lon ?? "");
  return (
    isFinite(latNum) &&
    isFinite(lonNum) &&
    latNum !== 0 &&
    lonNum !== 0 &&
    latNum >= -90 &&
    latNum <= 90 &&
    lonNum >= -180 &&
    lonNum <= 180
  );
}

/**
 * A stop that will NOT appear on the public site: `ambiguous`, `not_found`, or
 * not resolved yet (`""`) while the row HAS coordinates. A row without
 * coordinates is a geocoding problem, not this one.
 *
 * This is EXACTLY the server's `locality_problem=true` predicate
 * (`apps/localities/filters.py::locality_problem_q`, TASK-5167 + design Q2), so
 * the page-scope count and the filter-scope server count can never use two
 * different definitions.
 */
export function isLocalityProblem(row: {
  locality_status?: string | null;
  lat: string | number | null | undefined;
  lon: string | number | null | undefined;
}): boolean {
  const status = normaliseLocalityStatus(row.locality_status);
  if (status === "ambiguous" || status === "not_found") return true;
  return status === "" && hasValidCoords(row.lat, row.lon);
}

/**
 * Whether the LIST badge fires: `ambiguous` and `not_found` only (TASK-5174).
 * An unresolved `""` row gets no badge — it is a backfill gap, not a finding.
 */
export function isLocalityBadgeStatus(
  status: string | null | undefined,
): status is "ambiguous" | "not_found" {
  return status === "ambiguous" || status === "not_found";
}

/**
 * Distance for the `ambiguous` hint: below 1 km in whole metres («850 м»),
 * otherwise kilometres to one decimal («2,1 км»), localised by `Intl`.
 */
export function formatDistance(
  metres: number,
  language: string,
  units: { m: string; km: string },
): string {
  if (metres < 1000) {
    const n = new Intl.NumberFormat(language, {
      maximumFractionDigits: 0,
    }).format(Math.round(metres));
    return `${n} ${units.m}`;
  }
  const n = new Intl.NumberFormat(language, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(metres / 1000);
  return `${n} ${units.km}`;
}

/**
 * {@link isLocalityProblem} for a route-template editor stop. An UNKNOWN stop
 * (its card never loaded) is skipped, as `isUnroutableStop` does — reporting a
 * phantom would send the dispatcher after nothing.
 */
export function isLocalityProblemStop(stop: {
  unknown: boolean;
  localityStatus?: string;
  lat: number | null;
  lon: number | null;
}): boolean {
  return (
    !stop.unknown &&
    isLocalityProblem({
      locality_status: stop.localityStatus,
      lat: stop.lat,
      lon: stop.lon,
    })
  );
}
